import { writable, derived, type Readable, type Writable } from "svelte/store";
import type { Role, UserRole } from "@diy-avatar/shared/admin";

/** Roles allowed to use the admin app at all (students are rejected). */
export const ADMIN_APP_ROLES: Role[] = [
  "admin",
  "authority",
  "school-manager",
  "teacher",
];

export interface SessionUser {
  email: string;
  display_name: string | null;
  roles: UserRole[];
  /** Convenience derived flag. */
  isAdmin: boolean;
  /** When true the user must change their password before using the app. */
  mustChangePassword?: boolean;
}

/** Clear the force-change-password gate after a successful change. */
export function clearMustChangePassword(): void {
  session.update((s) =>
    s.user ? { ...s, user: { ...s.user, mustChangePassword: false } } : s,
  );
}

export interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
}

const initial: SessionState = {
  user: null,
  // Start in the loading state so protected route components don't mount (and
  // redirect to login) before the initial `/auth/me` check resolves on boot.
  loading: true,
  error: null,
};

export const session: Writable<SessionState> = writable(initial);

export function setUser(user: SessionUser | null): void {
  session.update((s) => ({ ...s, user, error: null }));
}

export function setLoading(loading: boolean): void {
  session.update((s) => ({ ...s, loading }));
}

export function setError(error: string | null): void {
  session.update((s) => ({ ...s, error }));
}

export function reset(): void {
  // Logout/clear: not loading (the boot check is long done), so the Router
  // renders the login page immediately instead of the boot overlay.
  session.set({ user: null, loading: false, error: null });
}

// ---- Role-derived access helpers ----

/**
 * Flattened, derived view of the current user's capabilities. Mirrors the
 * backend `UserScopes` shape but built from the wire `roles[]` returned by
 * `/auth/me`. Used purely for nav visibility + client-side gating; the backend
 * remains the source of truth for enforcement.
 */
export interface SessionScopes {
  authenticated: boolean;
  isAdmin: boolean;
  isAuthority: boolean;
  isSchoolManager: boolean;
  isTeacher: boolean;
  /** Authorities the user holds an `authority` role on. */
  accessibleAuthorityIds: number[];
  /** Schools the user can MANAGE (manager or authority-owned). */
  manageableSchoolIds: number[];
  /** Schools the user is a teacher in. */
  teacherSchoolIds: number[];
  /** Every school the user can access in any capacity. */
  accessibleSchoolIds: number[];
}

/** Build scope sets from a flat role list (admin: empty sets, isAdmin true). */
export function deriveScopes(roles: UserRole[] | null | undefined): SessionScopes {
  const out: SessionScopes = {
    authenticated: !!roles,
    isAdmin: false,
    isAuthority: false,
    isSchoolManager: false,
    isTeacher: false,
    accessibleAuthorityIds: [],
    manageableSchoolIds: [],
    teacherSchoolIds: [],
    accessibleSchoolIds: [],
  };
  if (!roles) return out;
  const authority = new Set<number>();
  const manage = new Set<number>();
  const teacher = new Set<number>();
  const access = new Set<number>();
  for (const r of roles) {
    switch (r.role) {
      case "admin":
        out.isAdmin = true;
        break;
      case "authority":
        out.isAuthority = true;
        if (r.authority_id != null) authority.add(r.authority_id);
        // Authority-owned schools are resolved server-side; the client knows
        // the authority and lets the backend scope the school list.
        break;
      case "school-manager":
        out.isSchoolManager = true;
        if (r.school_id != null) {
          manage.add(r.school_id);
          access.add(r.school_id);
        }
        break;
      case "teacher":
        out.isTeacher = true;
        if (r.school_id != null) {
          teacher.add(r.school_id);
          access.add(r.school_id);
        }
        break;
      case "student":
        if (r.school_id != null) access.add(r.school_id);
        break;
    }
  }
  out.accessibleAuthorityIds = [...authority];
  out.manageableSchoolIds = [...manage];
  out.teacherSchoolIds = [...teacher];
  out.accessibleSchoolIds = [...access];
  return out;
}

/** Reactive scopes derived from the session user's roles. */
export const scopes: Readable<SessionScopes> = derived(session, ($s) =>
  deriveScopes($s.user ? $s.user.roles : null),
);

export function hasRole(roles: UserRole[] | null | undefined, role: Role): boolean {
  return !!roles && roles.some((r) => r.role === role);
}

/** True when the user holds any role that may use the admin app. */
export function canUseAdminApp(roles: UserRole[] | null | undefined): boolean {
  return !!roles && roles.some((r) => ADMIN_APP_ROLES.includes(r.role));
}

/**
 * Role-appropriate landing route after sign-in. Only admins may use the
 * admin-only dashboard; everyone else is sent to a page their scopes allow, so
 * they never land on a screen that 403s. Mirrors the backend RBAC.
 */
export function landingPathFor(s: SessionScopes): string {
  if (s.isAdmin) return "/dashboard";
  if (s.isAuthority && s.accessibleAuthorityIds.length === 1) {
    return `/authorities/${s.accessibleAuthorityIds[0]}`;
  }
  if (s.isAuthority) return "/schools";
  // school-manager / teacher: jump straight to their school when there's one.
  if (s.accessibleSchoolIds.length === 1) {
    return `/schools/${s.accessibleSchoolIds[0]}`;
  }
  return "/schools";
}
